import React from 'react';
import { Avatar as AvatarRoot, AvatarFallback, AvatarImage } from '../../ui/avatar';

/**
 * AvatarNew - Migrated to shadcn/ui
 * 
 * Simple avatar wrapper component for displaying user profile images with fallback initials.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.src] - Image source URL
 * @param {string} [props.alt] - Alt text for image
 * @param {string} [props.fallback] - Fallback text (initials) when image fails to load
 * @param {string} [props.size='md'] - Size variant: 'sm', 'md', 'lg', 'xl'
 * @param {string} [props.className] - Additional CSS classes
 * 
 * @example
 * <AvatarNew
 *   src="/profile.jpg"
 *   alt="John Doe"
 *   fallback="JD"
 *   size="md"
 * />
 * 
 * Migration Notes:
 * - Uses shadcn Avatar, AvatarImage, AvatarFallback components
 * - Provides size variants with Tailwind classes
 * - Automatically displays fallback initials if image fails
 * - Compatible with MUI Avatar API (src, alt props)
 * 
 * Size Mapping:
 * - sm: h-8 w-8 (32px)
 * - md: h-10 w-10 (40px) - default
 * - lg: h-12 w-12 (48px)
 * - xl: h-16 w-16 (64px)
 */
function Avatar({ src, alt, fallback, size = 'md', className = '', ...props }) {
  // Size mapping
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg',
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  return (
    <AvatarRoot className={`${sizeClass} ${className}`} {...props}>
      {src && <AvatarImage src={src} alt={alt || 'Avatar'} />}
      <AvatarFallback className="bg-primary text-primary-foreground">
        {fallback || alt?.substring(0, 2).toUpperCase() || '??'}
      </AvatarFallback>
    </AvatarRoot>
  );
}

export default Avatar;
